// Shared company-profile vocabulary, used by employer onboarding, the company
// profile editor, and the public company page.

export const COMPANY_SIZES = [
  "1–10",
  "11–50",
  "51–200",
  "201–500",
  "500+",
] as const

export const COMPANY_STAGES = [
  "Bootstrapped",
  "Pre-seed",
  "Seed",
  "Series A",
  "Series B+",
  "Public",
] as const

/**
 * The company meta line parts — location · "N people" · stage — so every
 * surface says "people" the same way. Join with " · " to render.
 */
export function companyMetaParts(company: {
  location?: string | null
  size?: string | null
  stage?: string | null
}): string[] {
  return [
    company.location,
    company.size ? `${company.size} people` : undefined,
    company.stage,
  ].filter((part): part is string => Boolean(part))
}

/** Fields required to create a company and to submit it for verification. */
export const COMPANY_REQUIRED_PROFILE_FIELDS = [
  "name",
  "website",
  "linkedin",
] as const

export type CompanyRequiredProfileField =
  (typeof COMPANY_REQUIRED_PROFILE_FIELDS)[number]

export const COMPANY_REQUIRED_PROFILE_FIELD_LABELS: Record<
  CompanyRequiredProfileField,
  string
> = {
  name: "Company name",
  website: "Website",
  linkedin: "LinkedIn",
}

export function companyRequiredProfileMissingFields(company: {
  name?: string | null
  website?: string | null
  linkedin?: string | null
}): CompanyRequiredProfileField[] {
  return COMPANY_REQUIRED_PROFILE_FIELDS.filter(
    (field) => !company[field]?.trim(),
  )
}

export function companyRequiredProfileMissingLabels(company: {
  name?: string | null
  website?: string | null
  linkedin?: string | null
}): string[] {
  return companyRequiredProfileMissingFields(company).map(
    (field) => COMPANY_REQUIRED_PROFILE_FIELD_LABELS[field],
  )
}
